/**
 * The antd theme, lifted out of `main.tsx` so it can vary by writing direction.
 *
 * Only the typography changes between the two: colours, radii and component overrides are the
 * brand and must look identical in both languages.
 */

import { theme as antdThemeApi, type ThemeConfig } from 'antd';
import { tokens } from './tokens';
import { darkPalette as D } from './palette';

/**
 * Arabic typography.
 *
 * Poppins has no Arabic glyphs, so IBM Plex Sans Arabic leads the stack when the UI is Arabic and
 * Poppins stays behind it for the Latin runs that remain — employee names, codes, `AED`.
 *
 * The size and line-height bumps are not cosmetic. Arabic glyphs carry taller ascenders and deeper
 * descenders than Latin, and their apparent x-height reads smaller at the same nominal size, so a
 * Latin-tuned scale renders Arabic that looks small and clips diacritics in dense tables.
 */
const ARABIC_FONT_STACK =
  "'IBM Plex Sans Arabic', 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

/**
 * @param uiV2 the responsive/consistency switch (`src/theme/uiFlags.ts`). When on, component SIZES
 *   (control heights, card/tab/table padding) come from here instead of the `!important` rules in
 *   App.css. When off, the theme is byte-for-byte what it was.
 */
const buildLightTheme = (isRtl: boolean, uiV2 = false): ThemeConfig => ({
  token: {
    colorPrimary: tokens.color.primary,
    colorInfo: tokens.color.info,
    colorLink: tokens.color.primary,
    colorLinkHover: tokens.color.primaryDark,
    colorSuccess: tokens.color.primary,
    borderRadius: tokens.radius.md,
    borderRadiusLG: tokens.radius.lg,
    fontFamily: isRtl ? ARABIC_FONT_STACK : tokens.type.fontFamily,
    fontSize: isRtl ? tokens.type.size.base + 1 : tokens.type.size.base,
    lineHeight: isRtl ? 1.75 : 1.5715,
    colorBgLayout: tokens.color.bgLayout,
    /**
     * The surface every popup is painted on: Select and TreeSelect dropdowns, the
     * Dropdown menu, DatePicker panels, Cascader, Popover.
     *
     * Pinned rather than left to antd's default because it is the single token that
     * decides whether a floating menu is opaque. Without it the filter dropdowns read
     * as translucent and the table rows behind them bleed through the options, which
     * is illegible and looks unfinished. One token here beats patching each popup.
     */
    colorBgElevated: tokens.color.bgMain,
    controlHeight: 36,
    // UI v2: one control height for buttons, inputs, selects and pickers. v1 left SM/LG at antd's
    // defaults and App.css forced buttons to 40 / 32 / 48 with `!important`, so a button beside an
    // input was 4px taller than it.
    ...(uiV2 ? { controlHeightSM: 28, controlHeightLG: 40 } : {}),
  },
  components: {
    Button: {
      colorPrimary: tokens.color.primary,
      colorPrimaryHover: tokens.color.primaryDark,
      colorPrimaryActive: tokens.color.primaryDarker,
      algorithm: true as const,
      ...(uiV2 ? { paddingInline: 16, paddingInlineSM: 10, paddingInlineLG: 20 } : {}),
    },
    Menu: {
      itemBg: tokens.color.bgMain,
      subMenuItemBg: tokens.color.bgMain,
      itemSelectedBg: tokens.color.primary,
      itemHoverBg: tokens.color.primaryLight,
      itemSelectedColor: tokens.color.textWhite,
      itemHoverColor: tokens.color.primary,
      itemColor: tokens.color.textPrimary,
      iconSize: 16,
      // UI v2: a soft tint + brand-dark label marks the active item instead of a solid purple
      // slab (the rest of the look is in App.css's `shell` section, keyed off the same colours).
      ...(uiV2
        ? {
          itemSelectedBg: '#EEF2FF',
          itemSelectedColor: tokens.color.primaryDarker,
          itemHoverBg: '#F1F5F9',
          itemHeight: 36,
          itemBorderRadius: 8,
          subMenuItemBorderRadius: 8,
          collapsedIconSize: 16,
        }
        : {}),
    },
    Table: {
      headerBg: '#f8fafb',
      headerColor: '#44475b',
      // UI v2: 13px cells at every width. v1 was antd's 14px on desktop and App.css's 13/12/11 on
      // small screens, so the same table's text changed size three times as the window narrowed.
      ...(uiV2
        ? {
          cellFontSize: 13,
          cellFontSizeMD: 13,
          cellFontSizeSM: 13,
          cellPaddingBlock: 10,
          cellPaddingInline: 12,
          cellPaddingBlockMD: 8,
          cellPaddingInlineMD: 10,
          cellPaddingBlockSM: 6,
          cellPaddingInlineSM: 8,
        }
        : {}),
    },
    // UI v2: card padding lives here instead of App.css's `.ant-card-body { padding: 16px 20px
    // !important }`, which also overrode every per-card `styles.body` in the app.
    ...(uiV2
      ? { Card: { bodyPadding: 16, bodyPaddingSM: 12, headerPadding: 16, headerPaddingSM: 12 } }
      : {}),
    Tabs: {
      inkBarColor: tokens.color.primary,
      itemActiveColor: tokens.color.primary,
      itemSelectedColor: tokens.color.primary,
      itemHoverColor: tokens.color.primaryDark,
      ...(uiV2
        // 8px + 22px line + 8px = 38px: every tab strip in the app is 36-40px tall.
        ? { horizontalItemPadding: '8px 16px', horizontalItemPaddingSM: '6px 10px', horizontalItemPaddingLG: '10px 20px' }
        : {}),
    },
    Input: {
      activeBorderColor: tokens.color.primary,
      hoverBorderColor: tokens.color.primary,
      activeShadow: '0 0 0 2px rgba(79, 70, 229, 0.1)',
      // Prevent the inner <input> inside affix-wrapper from adding its own visible border
      paddingInline: 11,
      paddingBlock: 4,
    },
    Select: {
      optionSelectedBg: 'rgba(79, 70, 229, 0.1)',
      optionActiveBg: 'rgba(79, 70, 229, 0.06)',
    },
    Switch: {
      colorPrimary: tokens.color.primary,
      colorPrimaryHover: tokens.color.primaryDark,
    },
    Progress: {
      defaultColor: tokens.color.primary,
    },
    Steps: {
      colorPrimary: tokens.color.primary,
    },
  },
});

/**
 * Dark mode runs antd's dark algorithm over the same brand. The surface colours are set here so
 * antd's own components (tables, modals, inputs, menus) follow the same indigo-tinted dark as the
 * CSS variables in App.css (`html[data-theme="dark"]`).
 */
export const buildAntdTheme = (isRtl: boolean, uiV2 = false, dark = false): ThemeConfig => {
  const base = buildLightTheme(isRtl, uiV2);
  if (!dark) return base;
  return {
    ...base,
    algorithm: antdThemeApi.darkAlgorithm,
    token: {
      ...base.token,
      colorPrimary: D.accent,
      colorBgBase: D.page,
      colorBgContainer: D.surface,
      colorBgElevated: D.surfaceRaised,
      colorBgLayout: D.page,
      colorBorder: D.border,
      colorBorderSecondary: D.border,
      colorText: D.text,
      colorTextSecondary: D.textSecondary,
    },
    components: {
      ...base.components,
      Table: { ...base.components?.Table, headerBg: D.surfaceRaised, headerColor: D.railInk },
      Menu: { ...base.components?.Menu, itemBg: D.rail, itemSelectedBg: D.railActive, itemSelectedColor: '#FFFFFF', itemHoverBg: D.railHover, itemColor: D.railInk, itemHoverColor: '#FFFFFF' },
    },
  };
};
